import type { ApiResponse } from '@gokrew/models/generic';
import type { PaginationMeta } from '@gokrew/models/common';
import type { Employee } from '@gokrew/models/employee';
import type { LeaveRequestStatus } from '@gokrew/models/leaves';

/* ---------------------------------- */
/* Shared primitives */
/* ---------------------------------- */

export type SegmentType = 'CLOCK' | 'BREAK';

export interface ApiTimestamps {
  createdAt: string;
  updatedAt: string;
}

export interface ApiSoftDeletable extends ApiTimestamps {
  deletedAt: string | null;
}

/** { fromDate, toDate } shape used in API query params */
export interface ApiDateRange {
  fromDate: string;
  toDate: string;
}

/** Base fields shared by segment write payloads */
interface SegmentPayloadBase {
  type: SegmentType;
  startTime: string;
  endTime: string;
  employeeNote?: string;
  hrNote?: string;
}

export interface ApiEntity {
  id: string;
}

export interface ApiCompanyEntity extends ApiEntity {
  companyId: string;
}

export interface ApiEmployeeEntity extends ApiCompanyEntity {
  employeeId: string;
}

export interface ApiBranchedEntity extends ApiEmployeeEntity {
  branchId: string;
}

/* ---------------------------------- */
/* API response types */
/* ---------------------------------- */

export enum WeekDayStatus {
  Draft = 'DRAFT',
  Submitted = 'SUBMITTED',
  Approved = 'APPROVED',
  Rejected = 'REJECTED',
  Locked = 'LOCKED',
}

export type WeekDayType =
  | 'WORKING_FULL_DAY'
  | 'WORKING_HALF_DAY'
  | 'OFF'
  | 'WEEKLY_OFF'
  | 'HOLIDAY'
  | 'LEAVE_FULL'
  | 'HALF_DAY';

export interface ApiWeekDay extends ApiEntity {
  companyId?: string;
  employeeId?: string;
  branchId?: string;
  shiftPolicyId?: string | null;
  workDate: string;
  dayType: WeekDayType;
  dayTargetId?: string | null;
  scheduledStart?: string | null;
  scheduledEnd?: string | null;
  expectedMin?: number;
  breakAllowanceMin?: number;
  maxWorkingMin?: number;
  workedMin?: number;
  status: WeekDayStatus;
  submittedAt?: string | null;
  isLocked?: boolean;
  lockedByUserId?: string | null;
  lockedAt?: string | null;
  updatedByUserId?: string | null;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
  segments?: ApiTimesheetSegment[];
}

export interface ApiEmployeeUser extends ApiEntity, ApiTimestamps {
  firstName: string;
  lastName: string;
  profileImage: string | null;
  email: string;
  dateOfBirth: string | null;
  language: string;
  gender: string | null;
  phoneCountryCode: string | null;
  phoneNumber: string | null;
  phoneVerifiedAt: string | null;
  temporaryPasswordGeneratedAt: string | null;
  passwordChangedAt: string | null;
}

export interface ApiEmployeeDepartment extends ApiEntity {
  name: string;
}

export interface ApiEmployeeDesignation extends ApiEntity {
  name: string;
}

export interface ApiWeekApproval extends ApiEntity {
  status: 'PENDING' | 'APPROVED' | 'AUTO_APPROVED' | 'REJECTED' | 'LOCKED';
  totalWorkedMin: number;
  totalWorkableMin: number;
}

export interface ApiEmployeeWeekRecord
  extends ApiCompanyEntity, ApiSoftDeletable {
  userId: string;
  designationId: string | null;
  departmentId: string | null;
  branchId: string;
  employeeReferenceId: string | null;
  status: boolean;
  dateOfJoining: string;
  leaveEligibleFrom: string;
  employmentType: string;
  partTimeWeeklyMinutes?: number | null;
  isProbationEnabled: boolean;
  probationPeriod: number | null;
  probationEndDate: string | null;
  probationStatus: string | null;
  probationExtensionReason: string | null;
  probationReminderSentAt: string | null;
  workEmail: string | null;
  workPhone: string | null;
  terminatedAt: string | null;
  terminationEffectiveDate: string | null;
  terminationReason: string | null;
  terminationScheduledFor: string | null;
  terminationScheduledByUserId: string | null;
  loginAt: string | null;
  termsAndConditionsAcceptedAt: string | null;
  createdByUserId: string;
  updatedByUserId: string | null;
  lastInvitationSentAt: string | null;
  user: ApiEmployeeUser;
  department: ApiEmployeeDepartment | null;
  designation: ApiEmployeeDesignation | null;
  weekApproval: ApiWeekApproval | null;
  weekDays: ApiWeekDay[];
}

export type GetEmployeesWeekResponse = ApiResponse<{
  data: ApiEmployeeWeekRecord[];
  meta: PaginationMeta;
  weekStart: string;
  weekEnd: string;
}>;

export interface ApiTimesheetSegment extends ApiEntity, ApiSoftDeletable {
  employeeId: string;
  dayId: string;
  type: SegmentType;
  startTime: string;
  endTime: string | null;
  isOvernightSplit: boolean;
  splitGroupId: string | null;
  entryTimezone: string;
  source: string;
  employeeNote: string | null;
  hrNote: string | null;
  autoClosed: boolean;
}

export interface ApiTimesheetDay extends ApiEntity {
  companyId?: string;
  employeeId?: string;
  branchId?: string;
  workDate: string;
  dayType: WeekDayType;
  status: WeekDayStatus;
  scheduledStart?: string | null;
  scheduledEnd?: string | null;
  expectedMin?: number;
  workedMin?: number;
  breakAllowanceMin?: number;
  maxWorkingMin?: number;
  isLocked?: boolean;
  employeeNote?: string | null;
  hrNote?: string | null;
  segments: ApiTimesheetSegment[];
}

export interface TimesheetLeaveInfo {
  leaveRequestId: string;
  leaveTypeId: string | null;
  leaveTypeName: string | null;
  status: string;
  startDate: string;
  endDate: string;
  startSession: string | null;
  endSession: string | null;
  startTime: string | null;
  endTime: string | null;
  isHourly: boolean;
  days: number;
}

export interface TimesheetHolidayInfo {
  holidayId: string;
  name: string;
  description: string | null;
  date: string;
}

/** `leaves` and `holidays` cover the whole requested range, including dates
 *  with no day row behind them. */
export interface EmployeeRangeData {
  days: ApiTimesheetDay[];
  leaves: TimesheetLeaveInfo[];
  holidays: TimesheetHolidayInfo[];
}

/** `AM` / `PM` half-day boundary on a session-based leave. */
export type ApiDaySession = 'AM' | 'PM';

export interface ApiTimesheetLeave {
  leaveRequestId: string;
  leaveTypeId: string | null;
  leaveTypeName: string | null;
  status: LeaveRequestStatus;
  startDate: string;
  endDate: string;
  startSession: ApiDaySession | null;
  endSession: ApiDaySession | null;
  startTime: string | null;
  endTime: string | null;
  isHourly: boolean;
  days: number;
}

export interface ApiTimesheetHoliday {
  holidayId: string;
  name: string;
  description: string | null;
  date: string;
}

/** Leave and holiday state the API attaches to every day row it returns. */
export interface ApiDayLeaveState {
  onLeave: boolean;
  isHalfDay: boolean;
  isHourly: boolean;
  leaves: ApiTimesheetLeave[];
}

export interface ApiDayHolidayState {
  isHoliday: boolean;
  holidays: ApiTimesheetHoliday[];
}

export type ApiTimesheetRangeDay = ApiTimesheetDay &
  ApiDayLeaveState &
  ApiDayHolidayState;

/** `leaves` and `holidays` cover the whole requested range, including dates
 *  with no day row (future dates never have one). */
export type GetEmployeeRangeResponse = {
  message: string;
  data: EmployeeRangeData;
};

/* ---------------------------------- */
/* UI types */
/* ---------------------------------- */

export type TimesheetDayStatus =
  | 'full'
  | 'partial'
  | 'missing'
  | 'draft'
  | 'leave_full'
  | 'half_day'
  | 'holiday'
  | 'weekly_off';

export enum TimesheetApprovalStatus {
  Pending = 'pending',
  Approved = 'approved',
  AutoApproved = 'auto_approved',
  Rejected = 'rejected',
  Locked = 'locked',
}

export interface TimesheetDayEntry {
  status: TimesheetDayStatus;
  /** Colour treatment for `hours` when `status` isn't itself a worked state —
   *  a weekly off that was worked keeps its grey cell but reads like any
   *  other day. */
  hoursStatus?: 'full' | 'partial';
  /** A holiday that lands on a weekly off. The cell keeps the holiday label
   *  but reads as a day off underneath, rather than saying HOLIDAY twice. */
  onWeeklyOff?: boolean;
  hours?: string;
  overtime?: string;
  hasComment?: boolean;
  isLocked?: boolean;
}

export interface TimesheetEmployee {
  id: string;
  weekApprovalId: string | null;
  initials: string;
  name: string;
  role: string;
  entries: Record<string, TimesheetDayEntry>;
  totalHours: number;
  targetHours: number;
  weeklyTargetHours: number;
  missingDays: number;
  approvalStatus: TimesheetApprovalStatus;
}

export interface WeekDay {
  key: string;
  label: string;
  date: string;
  isToday?: boolean;
  isWeekend?: boolean;
}

export const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
export type Day = (typeof DAYS)[number];

export interface ShiftTimingValues {
  shiftStart: string;
  shiftEnd: string;
  dailyBreakMin: number;
  maxWorkingHours: number;
  lunchBuffer: number;
  workingDays: Day[];
}

export interface GetEmployeesWeekParams {
  page?: number;
  limit?: number;
  status?: 'all' | 'active' | 'inactive';
  branchId?: string;
  weekStart?: string;
  search?: string;
  departmentId?: string;
  designationId?: string;
}

export interface GetEmployeeRangeParams {
  dateRange?: ApiDateRange;
}

export interface GetRequestsParams {
  page?: number;
  limit?: number;
  branchId?: string;
  employeeId?: string;
  requestStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
  dateRange?: ApiDateRange;
}

export interface ApiTimesheetRequestSegment {
  startTime: string;
  endTime: string | null;
  type: SegmentType;
}

export interface ApiTimesheetRequest extends ApiEmployeeEntity, ApiTimestamps {
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  kind: string;
  workDate: string;
  reason: string | null;
  decisionNote: string | null;
  employee: Employee;
  proposed?: {
    resultSegments?: ApiTimesheetRequestSegment[];
    baseSegments?: ApiTimesheetRequestSegment[];
  };
}

export type GetRequestsResponse = {
  message: string;
  data: {
    data: ApiTimesheetRequest[];
    meta: PaginationMeta;
  };
};

export interface GetRequestsCountParams {
  employeeId?: string;
  requestStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
  dateRange?: ApiDateRange;
}

export type GetRequestsCountResponse = {
  message: string;
  data: {
    count: number;
  };
};

export interface UpdateSegmentPayload {
  startTime?: string;
  endTime?: string;
  employeeNote?: string;
  hrNote?: string;
}

export interface UpdateDayPayload {
  employeeNote?: string;
  hrNote?: string;
}

export interface CreateSegmentsPayload {
  segments: SegmentPayloadBase[];
}

export interface PatchEmployeeDaySegment extends SegmentPayloadBase {
  id?: string;
}

export interface PatchEmployeeDayPayload {
  segments: PatchEmployeeDaySegment[];
  deleteSegmentIds: string[];
}

export type GetEmployeeDayResponse = {
  message: string;
  data: ApiTimesheetDay;
};

export interface ApproveRejectPayload {
  note?: string;
}

export interface ExportTimesheetSummaryParams {
  dateRange: {
    fromDate: string;
    toDate: string;
  };
  search?: string;
  departmentId?: string;
  designationId?: string;
  format?: 'xlsx' | 'csv';
}

export interface ExportEmployeeTimesheetParams {
  dateRange: {
    fromDate: string;
    toDate: string;
  };
  format?: 'xlsx' | 'csv';
  includeRawSessions?: boolean;
}
